import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/locale-link";
import { track } from "@/lib/analytics";
import { useTranslation } from "@/hooks/useTranslation";
import { TRADE_JOIN_ROUTE } from "@/lib/homepage-ctas";
import { useJournalCta } from "@/hooks/useJournalCta";

/**
 * Closing conversion band — reinforces the personal Cigar Journal objective
 * with a single primary CTA, and keeps the Brand/Trade path available as a
 * clearly secondary action. Shared by desktop and mobile homepages.
 */
export function ClosingConversion({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const journalCta = useJournalCta();
  const consumerTo = journalCta.to;
  const surface = compact ? "home_mobile_closing" : "home_desktop_closing";

  return (
    <section className={compact ? "py-10 bg-card/40 border-y border-border/40" : "py-12 md:py-20 bg-card/40 border-y border-border/40"}>
      <div className="container">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-[24px] md:text-4xl font-semibold leading-tight">
            {t("homeClose.heading")}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mt-3 md:mt-4 leading-relaxed">
            {t("homeClose.body")}
          </p>

          <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3 sm:justify-center">
            <Link
              to={consumerTo}
              className="block sm:inline-block"
              onClick={() => {
                journalCta.onClick();
                track("audience_cta_clicked", {
                  audience: "consumer",
                  intent: "journal_start",
                  destination: consumerTo,
                  surface,
                  device: compact ? "mobile" : "desktop",
                });
              }}
            >
              <Button size="lg" className="w-full sm:w-auto min-h-[48px] gap-2 font-medium px-7">
                {t("homeClose.consumerCta")} <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link
              to={TRADE_JOIN_ROUTE}
              className="block sm:inline-block"
              onClick={() =>
                track("audience_cta_clicked", {
                  audience: "trade",
                  intent: "trade_join",
                  destination: TRADE_JOIN_ROUTE,
                  surface,
                  device: compact ? "mobile" : "desktop",
                })
              }
            >
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto min-h-[48px] font-medium px-7"
              >
                {t("homeClose.tradeCta")}
              </Button>
            </Link>
          </div>

          <p className="mt-4 text-xs md:text-sm text-muted-foreground">
            {t("homeClose.microcopy")}
          </p>
        </div>
      </div>
    </section>
  );
}
